import React, { useState, useMemo } from 'react'
import Tesseract from 'tesseract.js'
import * as pdfjs from 'pdfjs-dist'
import { useNavigate } from 'react-router-dom'
import { Tooltip } from '@mui/material'
import PreAuthForm from './PreAuthForm'
import { useTranslation } from 'react-i18next'
pdfjs.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'

const parseUcafText = text => {
  const extract = (pattern, defaultValue = null, group = 1) => {
    const match = text.match(pattern)
    if (!match || !match[group] || match[group].trim() === '')
      return defaultValue
    return match[group].trim()
  }
  const patterns = {
    providerName: /Provider\s*Name\s*[:|-\s]*([^\n]+?)(?=\s*Insured|$)/i,
    insuredName: /Insured\s*Name\s*[:|-\s]*([^\n]+?)(?=\s*(Insurance|Sex)|$)/i,
    insuranceCompany:
      /Insurance\s*Company\s*Name\s*[:|-\s]*([^\n]+?)(?=\s*ID|$)/i,
    idCardNumber: /ID\s*Card\s*Number\s*[:|-\s]*([\d]+)(?=\s*TPA|$)/i,
    tpaCompany: /TPA\s*Company\s*Name\s*[:|-\s]*([^\n]+?)(?=\s*Sex|$)/i,
    gender: /Sex\s*[:|-\s]*([A-Za-z]+)(?=\s|$)/i,
    dob: /Date\s*of\s*Birth\s*[:|-\s]*([\d]{4}-[\d]{2}-[\d]{2})(?=\s|$)/i,
    patientFileNo: /Patient\s*File\s*No\s*[:|-\s]*([\d]+)(?=\s|$)/i,
    policyHolder: /Policy\s*Holder\s*[:|-\s]*(YES|NO)(?=\s|$)/i,
    policyNumber: /Policy\s*Number\s*[:|-\s]*([\d]+)(?=\s|$)/i,
    planType: /Plan\s*Type\s*[:|-\s]*([^\n]+?)(?=\s*Date|$)/i,
    expiryDate:
      /Date\s*Of\s*Expiry\s*[:|-\s]*([\d]{4}-[\d]{2}-[\d]{2})(?=\s|$)/i,
    visitDate: /Date\s*Of\s*Visit\s*[:|-\s]*([^\n]+?)(?=\s*Approval|$)/i,
    approvalNumber: /Approval\s*[:|-\s]*([\d]+)(?=\s|$)/i,
    responseDate: /Response\s*Date\s*[:|-\s]*([^\n]+?)(?=\s*New|$)/i,
    approvalStatus: /Approval\s*Status\s*[:|-\s]*([^\n]+?)(?=\s*Approval|$)/i,
    referral:
      /Referrel\s*[:|-\s]*(YES|NO)(?:\s*Refer\s*To\s*[:|-\s]*([^\n]*?))?(?=\s*To\s*be|$)/i,
    emergencyLevel: /EmergencyCarelavel\s*[:|-\s]*([1-4])\s*\(\)/i,
    diagnosis: /Diagnosis\s*[:|-\s]*([^\n]+?)(?=\s*Please|$)/i,
    chronicCondition: /Chronic\s*\([Xx]\)/i,
    medication: /\((\d+(?:-\d+)?)\)\s*([^(]+?)(?=\s*\(|$)/g,
    lengthOfStay:
      /Estimated\s*length\s*of\s*Stay\s*[:|-\s]*([^\n]+?)(?=\s*I\s*hereby|$)/i,
    physicianSignature:
      /Physicians\s*Signature\s*[:|-\s]*([^\n]+?)(?=\s*Date|$)/i,
    approvalValidity:
      /Approval\s*validity\s*[:|-\s]*([\d]{2}\/[\d]{2}\/[\d]{4})\s*-\s*([\d]{2}\/[\d]{2}\/[\d]{4})\s*-\s*(\d+)\s*Days/i,
    vitalSigns: {
      bp: /BP[I]?\s*[:|-\s]*([^\n]+?)(?=\s*Pulse|$)/i,
      pulse: /Pulse\/?\s*[:|-\s]*([^\n]+?)(?=\s*Temp|$)/i,
      temp: /Temp[l]?\s*[:|-\s]*([^\n]+?)(?=\s*Weight|$)/i,
      weight: /Weight\/?\s*[:|-\s]*([^\n]+?)(?=\s*Height|$)/i,
      height: /Height\/?\s*[:|-\s]*([^\n]+?)(?=\s*RR|$)/i
    },
    chiefComplaint:
      /Cheif\s*Complaints\s*and\s*Main\s*Symptoms\s*[:|-\s]*([^\n]+?)(?=\s*Diagnosis|$)/i,
    managementPlan:
      /Suggestive\s*lines\s*of\s*Managment\s*[:|-\s]*([^\n]+?)(?=\s*Code|$)/i,
    physician: /Dr\.\s*([^\n]+?)(?=\s*O\.|$)/i
  }

  const medications = []
  let medMatch
  while ((medMatch = patterns.medication.exec(text)) !== null) {
    if (medMatch[1] && medMatch[2]) {
      medications.push({
        code: medMatch[1].trim(),
        description: medMatch[2].trim()
      })
    }
  }

  // Extract visit type with more specific checks
  const visitType = {
    new: /New\s*Visit\s*\([Xx\*]?\)/i.test(text),
    followUp: /FollowUp\s*\([Xx\*]?\)/i.test(text),
    referral: /Refell\s*\)[Xx\*]?/i.test(text),
    walkIn: /[Ww]alkin\s*\([Xx\*]?\)/i.test(text)
  }

  // Extract case type with more specific checks
  const caseType = {
    inpatient: /Inpatient\s*\([Xx\*]?\)/i.test(text),
    outpatient: /Outpatient\s*\([Xx\*]?\)/i.test(text),
    emergency: /EmergencyCase\s*\([Xx\*]?\)/i.test(text)
  }

  // Extract approval information with null safety
  const approvalMatch = text.match(patterns.approvalValidity)
  const approvalValidity = approvalMatch
    ? {
        start: approvalMatch[1]?.trim(),
        end: approvalMatch[2]?.trim(),
        days: approvalMatch[3]?.trim()
      }
    : null

  return {
    patientInfo: {
      fullName: extract(patterns.insuredName),
      gender: extract(patterns.gender),
      dateOfBirth: extract(patterns.dob),
      patientFileNumber: extract(patterns.patientFileNo),
      policyHolder: extract(patterns.policyHolder) === 'YES',
      idCardNumber: extract(patterns.idCardNumber)
    },
    insuranceInfo: {
      provider: extract(patterns.providerName),
      company: extract(patterns.insuranceCompany),
      tpaCompany: extract(patterns.tpaCompany),
      policyNumber: extract(patterns.policyNumber),
      planType: extract(patterns.planType),
      expiryDate: extract(patterns.expiryDate)
    },
    visitDetails: {
      date: extract(patterns.visitDate),
      type: visitType,
      caseType: caseType,
      emergencyLevel: extract(patterns.emergencyLevel),
      referral: {
        isReferred: extract(/Referrel\s*:\s*(YES|NO)/i) === 'YES',
        referredTo: extract(/Refer\s*To\s*:\s*([^:\n]*)/i)
      },
      vitalSigns: {
        bp: extract(patterns.vitalSigns.bp),
        pulse: extract(patterns.vitalSigns.pulse),
        temp: extract(patterns.vitalSigns.temp),
        weight: extract(patterns.vitalSigns.weight),
        height: extract(patterns.vitalSigns.height)
      }
    },
    medicalInfo: {
      chiefComplaint: extract(patterns.chiefComplaint),
      diagnosis: extract(patterns.diagnosis),
      isChronic: !!extract(patterns.chronicCondition),
      medications,
      managementPlan: extract(patterns.managementPlan)
    },
    approvalInfo: {
      number: extract(patterns.approvalNumber),
      status: extract(patterns.approvalStatus),
      responseDate: extract(patterns.responseDate),
      validity: approvalValidity,
      comments: extract(/Comments\s*:\s*([^:\n]+?)(?=\s*\(\*\)|$)/i)
    },
    care_teams: {
      name: extract(patterns.physician)
    },
    signature: extract(patterns.physicianSignature),
    date: extract(/Date\s*:\s*([^:\n]+?)(?=\s|$)/i)
  }
}

const OCRComponent = () => {
  const { t, i18n } = useTranslation()
  const [file, setFile] = useState(null)
  const [text, setText] = useState('')
  const [parsedData, setParsedData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const structuredData = useMemo(() => {
    return text ? parseUcafText(text) : null
  }, [text])

  const handleFileUpload = async event => {
    const uploadedFile = event.target.files[0]
    if (!uploadedFile) return
    setFile(uploadedFile?.name)
    setText('')
    setParsedData(null)

    if (uploadedFile.type === 'application/pdf') {
      await processPDF(uploadedFile)
    } else {
      await processImage(uploadedFile)
    }
  }

  const processPDF = async pdfFile => {
    setIsLoading(true)
    try {
      const pdf = await pdfjs.getDocument(URL.createObjectURL(pdfFile)).promise
      let fullText = ''
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum)
        const viewport = page.getViewport({ scale: 2.0 })
        const canvas = document.createElement('canvas')
        canvas.width = viewport.width
        canvas.height = viewport.height
        const context = canvas.getContext('2d')
        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise
        const imageData = canvas.toDataURL('image/png')
        const pageText = await extractTextFromImage(imageData)
        fullText += `${pageText}\n`
      }
      setText(fullText)
    } catch (error) {
      console.error(t('PDF processing error:'), error)
    }
    setIsLoading(false)
  }

  const processImage = async imageFile => {
    setIsLoading(true)
    const imageUrl = URL.createObjectURL(imageFile)
    const extractedText = await extractTextFromImage(imageUrl)
    setText(extractedText)
    setIsLoading(false)
  }

  const extractTextFromImage = async imageUrl => {
    const {
      data: { text }
    } = await Tesseract.recognize(imageUrl, 'eng')
    return text
  }

  return (
    <div className='eligibility_container'>
      <div className='eligibility-page'>
        <div className='flex justify-between flex-col lg:flex-row gap-4 mb-2 px-3'>
          <div class='benefeciary-top flex justify-center gap-2 items-center !p-0 !bg-transparent !shadow-none !border-none !mb-2'>
            <div
              className='bg-[#0070C2] p-1 rounded-full cursor-pointer'
              onClick={() => {
                navigate(-1)
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='none'
                className={`${i18n.language == 'ar' ? ' rotate-180' : ''}`}
              >
                <path
                  d='M9 19L10.41 17.59L5.83 13H22V11H5.83L10.42 6.41L9 5L2 12L9 19Z'
                  fill='white'
                />
              </svg>
            </div>
            <div className=''>
              <Tooltip
                placement='right'
                title='Keep track of your Beneficiary account'
                className='my-auto !mt-2'
              >
                <span className='font-semibold text-black/70 text-lg '>
                  {t('OCR')}
                  <br />
                </span>
              </Tooltip>
            </div>
          </div>

          <div>
            <label
              htmlFor='fileUpload'
              className='cursor-pointer rounded flex items-center justify-center gap-2 !bg-primary px-2 text-white !h-[28px]'
            >
              <span>{file ? file : t('Select some files')}</span>
            </label>
            <input
              type='file'
              id='fileUpload'
              accept='image/*, application/pdf'
              className='hidden'
              onChange={handleFileUpload}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className='flex items-center justify-center w-full px-3'>
          {isLoading && <span className='loader'></span>}
          {structuredData && <PreAuthForm item={structuredData} />}
        </div>
      </div>
    </div>
  )
}

export default OCRComponent
